import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as userApi from '../api/index';
import { demoLoginUser } from '../reduxStore/actions/userActionsHelper';
import { usersData$ } from '../reduxStore/selectors/userSelectors';

const IS_DEMO = process.env.REACT_APP_ENVCONFIG === 'demo';
export const DEMO_PRACTICE_LIMIT = 3;

const normalizeTrialValue = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(parsed, 0) : fallback;
};

const useDemoPracticeLimit = () => {
    const dispatch = useDispatch();
    const userData = useSelector(usersData$);

    const maxTrials = IS_DEMO && userData?.isDemo
        ? normalizeTrialValue(userData?.demo_max_trials, DEMO_PRACTICE_LIMIT)
        : DEMO_PRACTICE_LIMIT;

    const remainingTrials = IS_DEMO && userData?.isDemo
        ? normalizeTrialValue(userData?.demo_remaining_trials, maxTrials)
        : maxTrials;

    const count = Math.max(0, maxTrials - remainingTrials);
    const hasReachedLimit = IS_DEMO && userData?.isDemo && remainingTrials <= 0;

    const consumeTrial = useCallback(async () => {
        if (!IS_DEMO || !userData?.isDemo) {
            return { allowed: true, remainingTrials: maxTrials, maxTrials, user: userData };
        }

        if (remainingTrials <= 0) {
            return { allowed: false, remainingTrials: 0, maxTrials, user: userData };
        }

        const response = await userApi.consumeDemoTrial();
        const updatedUser = response?.data?.user || {
            ...userData,
            demo_remaining_trials: response?.data?.remainingTrials ?? Math.max(remainingTrials - 1, 0),
            demo_max_trials: response?.data?.maxTrials ?? maxTrials,
        };

        dispatch(demoLoginUser.success(updatedUser));

        return {
            allowed: true,
            remainingTrials: response?.data?.remainingTrials ?? updatedUser.demo_remaining_trials,
            maxTrials: response?.data?.maxTrials ?? updatedUser.demo_max_trials ?? maxTrials,
            user: updatedUser,
        };
    }, [dispatch, maxTrials, remainingTrials, userData]);

    return { count, remainingTrials, maxTrials, hasReachedLimit, consumeTrial };
};

export default useDemoPracticeLimit;
